import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { FocusOn, MoveFocusInside } from 'react-focus-on';
import { AFTER_APP } from 'Components/frameworks/portal/Portal.constants';
import { ICONS } from 'Components/utils/icon/icons';
import { BodyLocker } from 'Components/frameworks/body-locker/BodyLocker';
import { Icon } from 'Components/frameworks/icon/Icon';
import Portal from 'Components/frameworks/portal/Portal';
import { themes } from './Backdrop.themes';
type Props = {
  ariaLabel?: string;
  ariaLabelledby?: string;
  className?: string;
  closeLabel?: string;
  children: ReactNode;
  externalFocusTrapRefs?: Array<{
    current: HTMLElement | null | undefined;
  }>;
  hasCloseButton?: boolean;
  onClose?: (e: React.SyntheticEvent<any>) => void;
  portalName?: string;
  theme?: ThemeKey<typeof themes>;
};
export const Backdrop = ({
  ariaLabel,
  ariaLabelledby,
  children,
  className,
  closeLabel,
  externalFocusTrapRefs,
  hasCloseButton,
  onClose,
  portalName = AFTER_APP,
  theme = 'default',
}: Props) => {
  if (!Children.count(children)) {
    return null;
  }

  const { thCloseButton, thCloseButtonLabel } = themes[theme] as DefaultThemeStyles<typeof themes>;
  const closeBtnLabel = closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : closeLabel;
  const handleClose = onClose
    ? (event: Event | MouseEvent | TouchEvent) => {
        onClose(event as unknown as React.SyntheticEvent);
      }
    : undefined;
  return (
    <Portal name={portalName}>
      <BodyLocker isLocked>
        <div className="animation-duration-500 fixed inset-0 isolate z-3 animate-fadeIn">
          <div
            className={classnames('fixed inset-0 z-1 h-screen bg-black opacity-80', className)}
            data-testid="Backdrop-overlay"
          />

          <div
            aria-label={ariaLabelledby ? null : ariaLabel}
            aria-labelledby={ariaLabelledby}
            role="dialog"
          >
            <div role="document">
              {hasCloseButton ? (
                <FocusOn
                  allowPinchZoom
                  enabled
                  noIsolation
                  onEscapeKey={handleClose}
                  shards={externalFocusTrapRefs}
                >
                  <MoveFocusInside>
                    <button
                      className={classnames('absolute top-0 z-3', thCloseButton)}
                      data-testid="Backdrop-close"
                      onClick={onClose}
                      title={closeLabel && ariaLabel ? [closeLabel, ariaLabel].join(' ') : null}
                      type="button"
                    >
                      {closeBtnLabel && <span className={thCloseButtonLabel}>{closeBtnLabel}</span>}
                      <Icon name={ICONS.CROSS} width="1em" />
                    </button>
                  </MoveFocusInside>
                  <div className="absolute inset-0 z-2 flex items-center justify-center">
                    {children}
                  </div>
                </FocusOn>
              ) : (
                <div className="absolute inset-0 z-2 flex items-center justify-center p-20">
                  <FocusOn
                    allowPinchZoom
                    className="max-w-full"
                    enabled
                    noIsolation
                    onClickOutside={handleClose}
                    onEscapeKey={handleClose}
                    shards={externalFocusTrapRefs}
                  >
                    {children}
                  </FocusOn>
                </div>
              )}
            </div>
          </div>
        </div>
      </BodyLocker>
    </Portal>
  );
};
